import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { headerOffset } from '@/lib/scrollUnderHeader';

/**
 * Прокрутка при переходах по сайту. Роутер её не трогает: без этого новая
 * страница открывалась на той высоте, где была прежняя (плашка «Чакры
 * пары» вела на /chakry-pary на ~560 px вниз, форма — за верхом экрана).
 *
 *   — переход на другой адрес (ссылка, navigate, редирект) — в самый верх;
 *   — адрес с якорем (#id) — к якорю, под шапку; ленивую страницу ждём;
 *   — «Назад» / «Вперёд» браузера — на прежнюю позицию этой записи
 *     истории; страница грузится лениво — доводим, пока не дорастёт.
 *
 * Мгновенно, мимо scroll-behavior: smooth на html: плавная прокрутка
 * обрывается подменой страницы и останавливалась на середине.
 */

// Последняя позиция каждой записи истории. У первой записи и у записей от
// нативных #-ссылок ключ роутера 'default' — их различаем по адресу.
const positions = new Map();
const entryId = (loc) => (loc.key && loc.key !== 'default'
  ? loc.key : `default:${loc.pathname}${loc.search}${loc.hash}`);

// Мгновенно при любом CSS. Одной подмены инлайн-стиля мало: без пересчёта
// стиля Chrome брал smooth из CSS, плавная прокрутка обрывалась дорисовкой
// страницы, и /chakry-pary оставалась на 729 px вниз. Поэтому стиль
// пересчитываем явно и просим behavior: 'instant'; старый браузер, не
// знающий 'instant', прокручивает без параметра.
function jump(top) {
  const html = document.documentElement;
  const prev = html.style.scrollBehavior;
  html.style.scrollBehavior = 'auto';
  // Чтение стиля заставляет браузер пересчитать его до прокрутки.
  if (getComputedStyle(html).scrollBehavior !== 'auto') html.style.setProperty('scroll-behavior', 'auto', 'important');
  try {
    window.scrollTo({ top, left: 0, behavior: 'instant' });
  } catch (_e) {
    window.scrollTo(0, top);
  }
  html.style.scrollBehavior = prev;
}

function findAnchor(hash) {
  let id = hash.slice(1);
  try { id = decodeURIComponent(id); } catch (_e) { /* битый якорь — ищем как есть */ }
  if (!id) return null;
  return document.getElementById(id) || document.getElementsByName(id)[0] || null;
}

/** Повторяет попытку, пока она не удастся, до ~3 с; любое движение
 *  пользователя (колесо, касание, клавиша) — стоп. */
function retry(attempt) {
  if (attempt()) return () => {};
  let done = false;
  const stop = () => {
    done = true;
    clearInterval(timer);
    ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((e) => window.removeEventListener(e, stop));
  };
  const started = Date.now();
  const timer = setInterval(() => {
    if (done) return;
    if (attempt() || Date.now() - started > 3000) stop();
  }, 50);
  ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((e) => window.addEventListener(e, stop, { passive: true }));
  return stop;
}

export default function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();
  const prevPath = useRef(null);
  const id = entryId(location);

  // Запоминаем позицию текущей записи истории. Слушатель снимается ещё в
  // коммите перехода — прокрутка новой страницы старую запись не затрёт.
  useLayoutEffect(() => {
    const save = () => positions.set(id, window.scrollY);
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, [id]);

  useLayoutEffect(() => {
    const first = prevPath.current === null;
    const pathChanged = prevPath.current !== location.pathname;
    prevPath.current = location.pathname;

    if (navType === 'POP') {
      const saved = positions.get(id);
      if (typeof saved === 'number') {
        return retry(() => {
          jump(saved);
          return Math.abs(window.scrollY - saved) < 2;
        });
      }
      // Позиции нет — первая загрузка или нативная #-ссылка: прокрутку
      // ведёт браузер. Ленивой странице с якорем помогаем дождаться узла.
      if (!(first && location.hash)) return undefined;
    }

    if (location.hash) {
      return retry(() => {
        const el = findAnchor(location.hash);
        if (!el) return false;
        jump(Math.max(0, el.getBoundingClientRect().top + window.scrollY - headerOffset()));
        return true;
      });
    }

    if (pathChanged) jump(0);
    return undefined;
  }, [id, navType, location.pathname, location.hash]);

  return null;
}
